/**
 * What the Overview tab decides, as pure functions.
 *
 * Two rules live here rather than in the components, because each has an edge
 * that is worth stating once and asserting:
 *
 *   - the checklist's progress counts REQUIRED items only, and the card has
 *     nothing to say once they are all done
 *   - how automatic managed-board setup is explained before and after linking
 *
 * @module web/screens/app/overview/setup
 */
import type { ChecklistBlock, ChecklistItem } from "../../../api/types.ts";
import type { Hash } from "../../../routes.ts";

/**
 * What a row offers.
 *
 * Completed rows may link to the tab where the result lives.
 * An unlinked board has no action: setup is automatic at first confirmation.
 */
export type RowAction = { kind: "tab"; tab: Hash; cta: string };

export interface ChecklistCopy {
  label: string;
  sub: string;
  /** Offered while the item is still open. */
  open?: RowAction;
  /** Offered once it is done - usually the same tab, read-only wording. */
  done?: RowAction;
  /** Sub-line once done, where the open one would read as nonsense. */
  doneSub?: string;
}

/**
 * What each item is called, what it says, and where it is done.
 *
 * The rows used to be inert text: the card named four jobs and offered a way to
 * do exactly one of them. `.ck-act` had been in overview.css since B3 and was
 * never used in markup - the affordance was designed and never built.
 *
 * `content` is deliberately the only row with no action either way: it is done
 * in the form that follows its own row, so sending anyone to a tab would send
 * them away from the job.
 */
export const CK_COPY: Record<ChecklistItem["id"], ChecklistCopy> = {
  profile: {
    label: "Your profile",
    sub: "Brand name and contact email",
    open: { kind: "tab", tab: "settings", cta: "Edit" },
    done: { kind: "tab", tab: "settings", cta: "View" },
  },
  payment: {
    label: "Payment method",
    sub: "Added once - every brief after that is one tap",
    open: { kind: "tab", tab: "wallet", cta: "Add card" },
    done: { kind: "tab", tab: "wallet", cta: "View" },
  },
  content: {
    label: "Content folder",
    sub: "The Drive folder your editor cuts from",
  },
  board: {
    label: "Your board",
    sub: "We'll find yours or create one with your first confirmed brief",
    doneSub: "Linked - your briefs land on it",
    done: { kind: "tab", tab: "board", cta: "Open" },
  },
};

export interface Progress {
  done: number;
  total: number;
  /** Everything required is done, so the card has nothing left to say. */
  complete: boolean;
}

/**
 * Progress across the REQUIRED items.
 *
 * Optional items are badged and shown but never counted: "2 of 4" beside a list
 * where one of the four cannot block anything is a number that overstates what
 * is left to do.
 */
export function requiredProgress(block: ChecklistBlock | null | undefined): Progress {
  const required = (block?.items ?? []).filter(i => !i.optional);
  const done = required.filter(i => i.done).length;
  return { done, total: required.length, complete: required.length > 0 && done === required.length };
}

/** Whether the content-folder form is still worth offering. */
export function needsContentFolder(block: ChecklistBlock | null | undefined): boolean {
  const item = (block?.items ?? []).find(i => i.id === "content");
  return !!item && !item.done;
}
